import { useContext, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faBell,
  faChartLine,
  faLocationDot,
  faTable,
  faUsers,
} from "@fortawesome/pro-regular-svg-icons";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import PaginationControls from "../../common/PaginationControls";
import { AnalyticsWidgetUiDetailsDto } from "../../../types/analytics";
import { UsageAccountabilityExplorerDto } from "../../../types/analytics/AnalyticsPageDataDto";
import {
  UsageExplorerViewToken,
  UsageAttentionCardId,
} from "../../../types/analytics/UsageExplorerViewToken";
import { UsageExplorerLocationAggregate } from "../../../types/analytics/UsageExplorerLocationAggregate";
import { useCheckMobileScreen } from "../../../hooks";
import UserContext from "../../../state/UserContext";
import {
  buildUsageExplorerLocationLevel,
  filterUsageExplorerManagerRows,
} from "../../../helpers/usage/usageExplorerHelpers";
import UsageManagerDistribution from "./UsageManagerDistribution";
import UsageCompletionBar from "./UsageCompletionBar";
import UsageOutstandingCell from "./UsageOutstandingCell";
import UsageExplorerManagerCardList from "./UsageExplorerManagerCardList";
import UsageExplorerLocationCardList from "./UsageExplorerLocationCardList";
import { getCompletedSegmentColor } from "../../../helpers/usage/usageStatusChartHelpers";
import { UsageStatusDatasetItem } from "../../../types/analytics/UsageStatusDataset";
import AnalyticsFilterInputs from "../../../types/analytics/AnalyticsFilterInputs";
import UsageManagerNudgeCell from "./UsageManagerNudgeCell";
import UsageNudgeBatchComposeModal, {
  getActiveBatchStorageKey,
} from "./UsageNudgeBatchComposeModal";
import { isUsageNudgeOnCooldown } from "../../../helpers/usage/usageNudgeHelpers";
import { createDefaultExplorerViewToken } from "../../../helpers/usage/usageFocusHelpers";

const defaultPageSize = 10;

type ExplorerTab = "BY_MANAGER" | "BY_LOCATION";
type ManagerViewMode = "TABLE" | "CHART";

interface UsageAccountabilityExplorerProps {
  explorer: UsageAccountabilityExplorerDto;
  viewToken: UsageExplorerViewToken | null;
  onViewTokenChange: (token: UsageExplorerViewToken) => void;
  widget?: AnalyticsWidgetUiDetailsDto;
  filters?: AnalyticsFilterInputs | null;
  canSendNudges?: boolean;
}

function UsageAccountabilityExplorer({
  explorer,
  viewToken,
  onViewTokenChange,
  widget,
  filters = null,
  canSendNudges = false,
}: UsageAccountabilityExplorerProps) {
  const { t } = useTranslation();
  const userContext = useContext(UserContext);
  const clientColour = userContext.user.client.primaryHexColour;
  const isMobile = useCheckMobileScreen();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [selectedManagerIds, setSelectedManagerIds] = useState<Set<number>>(
    new Set(),
  );
  const [sentManagerIds, setSentManagerIds] = useState<Set<number>>(new Set());
  const [isBatchNudgeOpen, setIsBatchNudgeOpen] = useState(false);
  const [activeBatchId, setActiveBatchId] = useState<number | null>(() => {
    const stored = sessionStorage.getItem(getActiveBatchStorageKey("MANAGER"));
    const parsed = stored ? Number(stored) : 0;
    return parsed > 0 ? parsed : null;
  });
  const token = useMemo(
    () => viewToken ?? createDefaultExplorerViewToken(),
    [viewToken],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [
    token.lens,
    token.attentionCardId,
    token.managerId,
    token.searchText,
    token.locationBreadcrumbIds,
    pageSize,
  ]);

  const managerRows = useMemo(() => {
    const managerScopedRows = token.managerId
      ? explorer.managerRows.filter(
          (row) => row.managerEmployeeId === token.managerId,
        )
      : explorer.managerRows;

    return filterUsageExplorerManagerRows(
      managerScopedRows,
      token.attentionCardId,
      token.searchText,
    );
  }, [
    explorer.managerRows,
    token.attentionCardId,
    token.managerId,
    token.searchText,
  ]);

  const locationRows = useMemo(
    () =>
      buildUsageExplorerLocationLevel(
        explorer.locationRows,
        token.locationBreadcrumbIds,
        token.attentionCardId,
        token.searchText,
      ),
    [
      explorer.locationRows,
      token.attentionCardId,
      token.locationBreadcrumbIds,
      token.searchText,
    ],
  );

  const rows = token.lens === "BY_MANAGER" ? managerRows : locationRows;
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(currentPage, pageCount);
  const pageRows = rows.slice((safePage - 1) * pageSize, safePage * pageSize);
  const activeAttentionLabel = getAttentionLabel(token.attentionCardId, t);
  const breadcrumbs = getSelectedLocationBreadcrumbs(
    explorer,
    token.locationBreadcrumbIds,
  );

  const updateToken = (updates: Partial<UsageExplorerViewToken>) => {
    onViewTokenChange({ ...token, ...updates });
  };

  const activeTab: ExplorerTab =
    token.lens === "BY_LOCATION" ? "BY_LOCATION" : "BY_MANAGER";

  const managerViewMode: ManagerViewMode = token.showDistribution
    ? "CHART"
    : "TABLE";

  const setActiveTab = (tab: ExplorerTab) => {
    if (tab === "BY_MANAGER") {
      updateToken({
        lens: "BY_MANAGER",
        showDistribution: false,
        locationBreadcrumbIds: [],
        managerId: undefined,
        searchText: "",
      });
      return;
    }

    updateToken({
      lens: "BY_LOCATION",
      showDistribution: false,
      locationBreadcrumbIds: [],
      managerId: undefined,
      searchText: "",
    });
  };

  const setManagerViewMode = (mode: ManagerViewMode) => {
    updateToken({ showDistribution: mode === "CHART" });
  };

  const completedColor = getCompletedSegmentColor(
    (widget?.datasets?.[0]?.items ?? []) as UsageStatusDatasetItem[],
  );

  const focusManagerRow = (managerId: number) => {
    updateToken({
      lens: "BY_MANAGER",
      managerId,
      attentionCardId: undefined,
      searchText: "",
      showDistribution: false,
    });
    window.setTimeout(() => {
      document.getElementById(`usage-manager-row-${managerId}`)?.focus();
    }, 0);
  };

  const byManagerTabTitle = t(
    "Analytics.Widgets.Usage.Explorer.ByManager",
    "By manager",
  );
  const byLocationTabTitle = t(
    "Analytics.Widgets.Usage.Explorer.ByLocation",
    "By location",
  );
  const themeColor = clientColour ? `#${clientColour}` : undefined;

  useEffect(() => {
    const visibleManagerIds = new Set(
      managerRows.map((row) => row.managerEmployeeId),
    );
    setSelectedManagerIds(
      (current) =>
        new Set(Array.from(current).filter((id) => visibleManagerIds.has(id))),
    );
  }, [managerRows]);

  const isManagerSelectable = (
    row: UsageAccountabilityExplorerDto["managerRows"][number],
  ) =>
    row.outstandingCount > 0 &&
    !sentManagerIds.has(row.managerEmployeeId) &&
    !isUsageNudgeOnCooldown(row.lastManagerNudgeSentAt);

  const toggleManagerSelection = (managerEmployeeId: number) => {
    setSelectedManagerIds((current) => {
      const next = new Set(current);
      if (next.has(managerEmployeeId)) {
        next.delete(managerEmployeeId);
      } else {
        next.add(managerEmployeeId);
      }
      return next;
    });
  };

  const lensToggle = (
    <SegmentedButtonGroup
      ariaLabel={t(
        "Analytics.Widgets.Usage.Explorer.LensLabel",
        "Explorer view",
      )}
      value={activeTab}
      onChange={setActiveTab}
      themeColor={themeColor}
      options={[
        {
          value: "BY_MANAGER",
          label: byManagerTabTitle,
          icon: faUsers,
        },
        {
          value: "BY_LOCATION",
          label: byLocationTabTitle,
          icon: faLocationDot,
        },
      ]}
    />
  );

  const managerViewToggle = (
    <SegmentedButtonGroup
      ariaLabel={t(
        "Analytics.Widgets.Usage.Explorer.ManagerViewToggle",
        "Manager view",
      )}
      value={managerViewMode}
      onChange={setManagerViewMode}
      themeColor={themeColor}
      options={[
        {
          value: "TABLE",
          label: t("Analytics.Widgets.Usage.Explorer.Table", "Table"),
          icon: faTable,
        },
        {
          value: "CHART",
          label: t("Analytics.Widgets.Usage.Explorer.Chart", "Chart"),
          icon: faChartLine,
        },
      ]}
    />
  );

  const managerExplorerToolbar = (
    <div className="flex flex-wrap items-center gap-3">
      <label className="min-w-0 flex-1 basis-full sm:basis-auto sm:max-w-md">
        <span className="sr-only">
          {t("Analytics.Widgets.Usage.Explorer.SearchLabel", "Search rows")}
        </span>
        <input
          type="search"
          value={token.searchText}
          onChange={(event) =>
            updateToken({
              searchText: event.target.value,
              managerId: undefined,
            })
          }
          placeholder={t(
            "Analytics.Widgets.Usage.Explorer.SearchManagers",
            "Search managers",
          )}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-800 placeholder:not-italic focus:border-primary focus:outline-none focus:ring-1 focus:ring-[color:var(--color-primary)]"
        />
      </label>

      {activeAttentionLabel && (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>
            {t(
              "Analytics.Widgets.Usage.Explorer.AttentionFilter",
              "Showing: {{filter}}",
              { filter: activeAttentionLabel },
            )}
          </span>
          <button
            type="button"
            className="font-semibold text-primary underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
            onClick={() =>
              updateToken({
                attentionCardId: undefined,
                managerId: undefined,
              })
            }
          >
            {t("Common.Clear", "Clear")}
          </button>
        </div>
      )}

      <div className="ml-auto flex shrink-0 items-center gap-2">
        {managerViewToggle}
      </div>
    </div>
  );

  const showManagerBatchNudgeBar =
    canSendNudges &&
    managerViewMode === "TABLE" &&
    !isMobile &&
    selectedManagerIds.size > 0;

  const managerBatchNudgeBar = showManagerBatchNudgeBar && (
    <div className="mt-3 flex flex-wrap items-center gap-3">
      <button
        type="button"
        className="btn-secondary btn-small inline-flex items-center gap-1.5"
        onClick={() => setIsBatchNudgeOpen(true)}
      >
        <FontAwesomeIcon icon={faBell} aria-hidden />
        {t("Analytics.Widgets.Usage.Nudge.Selected", {
          defaultValue: "Nudge selected ({{count}})",
          count: selectedManagerIds.size,
        })}
      </button>
    </div>
  );

  const activeBatchBanner = activeBatchId && !isBatchNudgeOpen && (
    <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
      {t(
        "Analytics.Widgets.Usage.Nudge.ActiveBatchBanner",
        "A bulk nudge is still sending in the background.",
      )}{" "}
      <button
        type="button"
        className="font-semibold underline"
        onClick={() => setIsBatchNudgeOpen(true)}
      >
        {t("Analytics.Widgets.Usage.Nudge.ViewProgress", "View progress")}
      </button>
    </div>
  );

  const managerTablePanel = (
    <>
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        {isMobile ? (
          <UsageExplorerManagerCardList
            rows={pageRows as typeof managerRows}
            completedColor={completedColor}
            onSelectManager={focusManagerRow}
            filters={filters}
            canSendNudges={canSendNudges}
          />
        ) : (
          <ManagerTable
            rows={pageRows as typeof managerRows}
            completedColor={completedColor}
            filters={filters}
            canSendNudges={canSendNudges}
            selectedManagerIds={selectedManagerIds}
            sentManagerIds={sentManagerIds}
            isManagerSelectable={isManagerSelectable}
            onToggleManagerSelection={toggleManagerSelection}
            onManagerSent={(managerEmployeeId) => {
              setSentManagerIds((current) => {
                const next = new Set(current);
                next.add(managerEmployeeId);
                return next;
              });
              setSelectedManagerIds((current) => {
                const next = new Set(current);
                next.delete(managerEmployeeId);
                return next;
              });
            }}
            onSelectPage={(managerEmployeeIds, selected) =>
              setSelectedManagerIds((current) => {
                const next = new Set(current);
                managerEmployeeIds.forEach((id) =>
                  selected ? next.add(id) : next.delete(id),
                );
                return next;
              })
            }
          />
        )}
      </div>
      {managerBatchNudgeBar}
      {activeBatchBanner}
      {managerRows.length > 0 && (
        <PaginationControls
          activePage={safePage}
          itemsPerPage={pageSize}
          totalItemCount={managerRows.length}
          onPageChange={setCurrentPage}
          onPageSizeChange={(nextPageSize) => {
            setPageSize(nextPageSize);
            setCurrentPage(1);
          }}
        />
      )}
    </>
  );

  const locationExplorerToolbar = (searchPlaceholder: string) => (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <label className="min-w-0 flex-1 sm:max-w-md">
        <span className="sr-only">
          {t("Analytics.Widgets.Usage.Explorer.SearchLabel", "Search rows")}
        </span>
        <input
          type="search"
          value={token.searchText}
          onChange={(event) =>
            updateToken({
              searchText: event.target.value,
              managerId: undefined,
            })
          }
          placeholder={searchPlaceholder}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-800 placeholder:not-italic focus:border-primary focus:outline-none focus:ring-1 focus:ring-[color:var(--color-primary)]"
        />
      </label>

      {activeAttentionLabel && (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>
            {t(
              "Analytics.Widgets.Usage.Explorer.AttentionFilter",
              "Showing: {{filter}}",
              { filter: activeAttentionLabel },
            )}
          </span>
          <button
            type="button"
            className="font-semibold text-primary underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
            onClick={() =>
              updateToken({
                attentionCardId: undefined,
                managerId: undefined,
              })
            }
          >
            {t("Common.Clear", "Clear")}
          </button>
        </div>
      )}
    </div>
  );

  const locationTablePanel = (
    <>
      <LocationBreadcrumbs
        breadcrumbs={breadcrumbs}
        onNavigate={(locationBreadcrumbIds) =>
          updateToken({ locationBreadcrumbIds, searchText: "" })
        }
      />
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        {isMobile ? (
          <UsageExplorerLocationCardList
            rows={pageRows as UsageExplorerLocationAggregate[]}
            onOpenLocation={(locationId) =>
              updateToken({
                locationBreadcrumbIds: [
                  ...token.locationBreadcrumbIds,
                  locationId,
                ],
                searchText: "",
              })
            }
          />
        ) : (
          <LocationTable
            rows={pageRows as UsageExplorerLocationAggregate[]}
            onOpenLocation={(locationId) =>
              updateToken({
                locationBreadcrumbIds: [
                  ...token.locationBreadcrumbIds,
                  locationId,
                ],
                searchText: "",
              })
            }
          />
        )}
      </div>
      {locationRows.length > 0 && (
        <PaginationControls
          activePage={safePage}
          itemsPerPage={pageSize}
          totalItemCount={locationRows.length}
          onPageChange={setCurrentPage}
          onPageSizeChange={(nextPageSize) => {
            setPageSize(nextPageSize);
            setCurrentPage(1);
          }}
        />
      )}
    </>
  );

  return (
    <section className="mx-3 mt-4" aria-labelledby="usage-explorer-heading">
      <div>
        <h2
          id="usage-explorer-heading"
          className="font-heading text-xl font-semibold text-gray-700"
        >
          {t("Analytics.Widgets.Usage.Explorer.Heading", "Incomplete journeys")}
        </h2>
        <p className="mt-1 max-w-3xl text-sm text-gray-600">
          {t(
            "Analytics.Widgets.Usage.Explorer.Description",
            "The managers and locations with journeys still open - send a nudge to get them moving",
          )}
        </p>
      </div>

      <div id="usage-explorer-table-anchor" className="mt-4">
        {lensToggle}
        <div className="mt-4 space-y-4">
          {activeTab === "BY_MANAGER" ? (
            <>
              {managerExplorerToolbar}
              {managerViewMode === "CHART" ? (
                <UsageManagerDistribution
                  managerRows={managerRows}
                  isMobile={isMobile}
                  onSelectManager={focusManagerRow}
                />
              ) : (
                managerTablePanel
              )}
            </>
          ) : (
            <>
              {locationExplorerToolbar(
                t(
                  "Analytics.Widgets.Usage.Explorer.SearchLocations",
                  "Search this location level",
                ),
              )}
              {locationTablePanel}
            </>
          )}
        </div>
      </div>
      <UsageNudgeBatchComposeModal
        isOpen={isBatchNudgeOpen}
        onOpenChange={setIsBatchNudgeOpen}
        filters={filters}
        recipientEmployeeIds={Array.from(selectedManagerIds)}
        audience="MANAGER"
        resumeBatchId={activeBatchId}
        onActiveBatchChange={setActiveBatchId}
        onSent={(recipientEmployeeIds) => {
          setSentManagerIds(
            (current) =>
              new Set(Array.from(current).concat(recipientEmployeeIds)),
          );
          setSelectedManagerIds(new Set());
        }}
      />
    </section>
  );
}

function SegmentedButtonGroup<T extends string>({
  ariaLabel,
  value,
  onChange,
  themeColor,
  options,
}: {
  ariaLabel: string;
  value: T;
  onChange: (value: T) => void;
  themeColor?: string;
  options: { value: T; label: string; icon: IconDefinition }[];
}) {
  return (
    <div
      className="inline-flex rounded-md shadow-sm"
      role="group"
      aria-label={ariaLabel}
    >
      {options.map((option, index) => {
        const isSelected = option.value === value;
        const isFirst = index === 0;
        const isLast = index === options.length - 1;

        return (
          <button
            key={option.value}
            type="button"
            className={cx(
              "px-4 py-2 text-sm font-medium bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 hover:text-gray-900 disabled:text-gray-400 disabled:cursor-not-allowed disabled:bg-gray-50 transition-all duration-150",
              isFirst && "rounded-l-md",
              isLast && "rounded-r-md",
              !isFirst && "-ml-px",
              isSelected && "relative z-10",
            )}
            onClick={() => onChange(option.value)}
            disabled={isSelected}
            style={
              isSelected && themeColor
                ? {
                    backgroundColor: `${themeColor}10`,
                    borderColor: themeColor,
                    color: themeColor,
                  }
                : undefined
            }
          >
            <FontAwesomeIcon
              icon={option.icon}
              size="xs"
              className="mr-1.5"
              aria-hidden
            />
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function ManagerTable({
  rows,
  completedColor,
  filters,
  canSendNudges,
  selectedManagerIds,
  sentManagerIds,
  isManagerSelectable,
  onToggleManagerSelection,
  onManagerSent,
  onSelectPage,
}: {
  rows: UsageAccountabilityExplorerDto["managerRows"];
  completedColor: string;
  filters: AnalyticsFilterInputs | null;
  canSendNudges: boolean;
  selectedManagerIds: Set<number>;
  sentManagerIds: Set<number>;
  isManagerSelectable: (
    row: UsageAccountabilityExplorerDto["managerRows"][number],
  ) => boolean;
  onToggleManagerSelection: (managerEmployeeId: number) => void;
  onManagerSent: (managerEmployeeId: number) => void;
  onSelectPage: (managerEmployeeIds: number[], selected: boolean) => void;
}) {
  const { t } = useTranslation();
  const eligiblePageIds = rows
    .filter(isManagerSelectable)
    .map((row) => row.managerEmployeeId);
  const allEligiblePageRowsSelected =
    eligiblePageIds.length > 0 &&
    eligiblePageIds.every((id) => selectedManagerIds.has(id));

  return (
    <table className="min-w-[680px] w-full text-left text-sm">
      <caption className="sr-only">
        {t(
          "Analytics.Widgets.Usage.Explorer.ManagerTableCaption",
          "Manager accountability ranking",
        )}
      </caption>
      <thead className="bg-gray-50 text-xs font-semibold text-gray-600">
        <tr>
          {canSendNudges && (
            <th
              scope="col"
              className={cx(
                "w-10 px-3 py-3 text-center",
                eligiblePageIds.length === 0 && "cursor-not-allowed",
              )}
            >
              <input
                type="checkbox"
                aria-label={t(
                  "Analytics.Widgets.Usage.Nudge.SelectEligibleManagers",
                  "Select eligible managers on this page",
                )}
                checked={allEligiblePageRowsSelected}
                disabled={eligiblePageIds.length === 0}
                onChange={(event) =>
                  onSelectPage(eligiblePageIds, event.target.checked)
                }
                className="rounded border-gray-300 text-primary focus:ring-[color:var(--color-primary)] disabled:cursor-not-allowed"
              />
            </th>
          )}
          <th scope="col" className="px-4 py-3">
            {t("Analytics.Widgets.Usage.Explorer.Manager", "Manager")}
          </th>
          <th scope="col" className="px-4 py-3 text-right">
            {t("Analytics.Widgets.Usage.Explorer.TeamSize", "Team")}
          </th>
          <th scope="col" className="px-4 py-3 text-right">
            {t("Analytics.Widgets.Usage.Explorer.Outstanding", "Outstanding")}
          </th>
          <th scope="col" className="px-4 py-3 text-right">
            {t(
              "Analytics.Widgets.Usage.Explorer.OldestWaitingOnManager",
              "Oldest waiting on manager",
            )}
          </th>
          <th scope="col" className="px-4 py-3 text-right">
            {t(
              "Analytics.Widgets.Usage.Explorer.OldestOutstanding",
              "Oldest outstanding",
            )}
          </th>
          <th scope="col" className="px-4 py-3 text-right">
            {t("Analytics.Widgets.Usage.Explorer.Completion", "Completion")}
          </th>
          {canSendNudges && (
            <th scope="col" className="px-4 py-3 text-right">
              {t("Analytics.Widgets.Usage.Explorer.Nudge", "Nudge")}
            </th>
          )}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {rows.map((row) => (
          <tr key={row.managerEmployeeId} className="text-gray-700">
            {canSendNudges && (
              <td
                className={cx(
                  "w-10 px-3 py-3 text-center",
                  !isManagerSelectable(row) && "cursor-not-allowed",
                )}
              >
                <input
                  type="checkbox"
                  aria-label={t("Analytics.Widgets.Usage.Nudge.SelectManager", {
                    defaultValue: "Select {{name}}",
                    name:
                      row.managerName ||
                      t(
                        "Analytics.Widgets.Usage.Explorer.UnknownManager",
                        "Unknown manager",
                      ),
                  })}
                  checked={selectedManagerIds.has(row.managerEmployeeId)}
                  disabled={!isManagerSelectable(row)}
                  onChange={() =>
                    onToggleManagerSelection(row.managerEmployeeId)
                  }
                  className="rounded border-gray-300 text-primary focus:ring-[color:var(--color-primary)] disabled:cursor-not-allowed"
                />
              </td>
            )}
            <th
              id={`usage-manager-row-${row.managerEmployeeId}`}
              scope="row"
              tabIndex={-1}
              className="px-4 py-3 font-semibold text-gray-800 focus:outline-2 focus:outline-offset-[-2px] focus:outline-[color:var(--color-primary)]"
            >
              {row.managerName ||
                t(
                  "Analytics.Widgets.Usage.Explorer.UnknownManager",
                  "Unknown manager",
                )}
            </th>
            <td className="px-4 py-3 text-right">{row.teamSize}</td>
            <td className="px-4 py-3 text-right">
              <UsageOutstandingCell
                outstandingCount={row.outstandingCount}
                outstandingInFilterCount={row.outstandingInFilterCount}
              />
            </td>
            <td className="px-4 py-3 text-right">
              <AgeValue days={row.oldestWaitingOnManagerAgeDays} />
            </td>
            <td className="px-4 py-3 text-right">
              <AgeValue days={row.oldestOutstandingAgeDays} />
            </td>
            <td className="px-4 py-3 text-right">
              <UsageCompletionBar
                percent={row.completionPercent}
                fillColor={completedColor}
              />
            </td>
            {canSendNudges && (
              <td className="px-4 py-3 text-right">
                <UsageManagerNudgeCell
                  row={row}
                  filters={filters}
                  lastSentAtOverride={
                    sentManagerIds.has(row.managerEmployeeId)
                      ? new Date().toISOString()
                      : undefined
                  }
                  onSent={() => onManagerSent(row.managerEmployeeId)}
                />
              </td>
            )}
          </tr>
        ))}
        {rows.length === 0 && (
          <EmptyTableRow columnCount={canSendNudges ? 8 : 6} />
        )}
      </tbody>
    </table>
  );
}

function LocationTable({
  rows,
  onOpenLocation,
}: {
  rows: UsageExplorerLocationAggregate[];
  onOpenLocation: (locationId: number) => void;
}) {
  const { t } = useTranslation();

  return (
    <table className="min-w-[760px] w-full text-left text-sm">
      <caption className="sr-only">
        {t(
          "Analytics.Widgets.Usage.Explorer.LocationTableCaption",
          "Location accountability summary",
        )}
      </caption>
      <thead className="bg-gray-50 text-xs font-semibold text-gray-600">
        <tr>
          <th scope="col" className="px-4 py-3">
            {t("Analytics.Widgets.Usage.Explorer.Location", "Location")}
          </th>
          <th scope="col" className="px-4 py-3 text-right">
            {t("Analytics.Widgets.Usage.Explorer.Assigned", "Assigned")}
          </th>
          <th scope="col" className="px-4 py-3 text-right">
            {t("Analytics.Widgets.Usage.Explorer.Outstanding", "Outstanding")}
          </th>
          <th scope="col" className="px-4 py-3 text-right">
            {t("Analytics.Widgets.Usage.Explorer.NotStarted", "Not started")}
          </th>
          <th scope="col" className="px-4 py-3 text-right">
            {t(
              "Analytics.Widgets.Usage.Explorer.WaitingOnManager",
              "Waiting on manager 14+ days",
            )}
          </th>
          <th scope="col" className="px-4 py-3 text-right">
            {t(
              "Analytics.Widgets.Usage.Explorer.OldestOutstanding",
              "Oldest outstanding",
            )}
          </th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {rows.map((row) => (
          <tr key={row.locationId ?? "unassigned"} className="text-gray-700">
            <th scope="row" className="px-4 py-3 font-semibold text-gray-800">
              {row.hasChildren && row.locationId !== null ? (
                <button
                  type="button"
                  className="font-semibold text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                  onClick={() => onOpenLocation(row.locationId!)}
                >
                  {row.locationName}
                </button>
              ) : (
                <>
                  {row.locationName ||
                    t(
                      "Analytics.Widgets.Usage.Explorer.NoLocation",
                      "No location",
                    )}
                  {row.isCurrentLocation && (
                    <span className="ml-1 font-normal text-gray-500">
                      {t(
                        "Analytics.Widgets.Usage.Explorer.ThisLocation",
                        "(this location)",
                      )}
                    </span>
                  )}
                </>
              )}
            </th>
            <td className="px-4 py-3 text-right">{row.assignedCount}</td>
            <td className="px-4 py-3 text-right font-semibold">
              {row.outstandingCount}
            </td>
            <td className="px-4 py-3 text-right">{row.notStartedCount}</td>
            <td className="px-4 py-3 text-right">
              {row.waitingOnManagerStalledCount}
            </td>
            <td className="px-4 py-3 text-right">
              <AgeValue days={row.oldestOutstandingAgeDays} />
            </td>
          </tr>
        ))}
        {rows.length === 0 && <EmptyTableRow columnCount={6} />}
      </tbody>
    </table>
  );
}

function AgeValue({ days }: { days: number | null }) {
  const { t } = useTranslation();
  if (days === null) return <>-</>;

  return (
    <>
      {days === 1
        ? t("Analytics.Widgets.Usage.Explorer.OneDay", "1 day")
        : t("Analytics.Widgets.Usage.Explorer.Days", "{{count}} days", {
            count: days,
          })}
    </>
  );
}

function EmptyTableRow({ columnCount }: { columnCount: number }) {
  const { t } = useTranslation();
  return (
    <tr>
      <td
        colSpan={columnCount}
        className="px-4 py-10 text-center text-sm text-gray-600"
      >
        {t(
          "Analytics.Widgets.Usage.Explorer.Empty",
          "No rows match this explorer view.",
        )}
      </td>
    </tr>
  );
}

function LocationBreadcrumbs({
  breadcrumbs,
  onNavigate,
}: {
  breadcrumbs: { locationId: number; locationName: string }[];
  onNavigate: (breadcrumbIds: number[]) => void;
}) {
  const { t } = useTranslation();
  return (
    <nav
      className="mt-4 flex flex-wrap items-center gap-2 text-sm"
      aria-label={t(
        "Analytics.Widgets.Usage.Explorer.LocationBreadcrumbs",
        "Location breadcrumbs",
      )}
    >
      {breadcrumbs.length === 0 ? (
        <span className="font-semibold text-gray-700" aria-current="page">
          {t("Analytics.Widgets.Usage.Explorer.AllLocations", "All locations")}
        </span>
      ) : (
        <button
          type="button"
          className="font-semibold text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
          onClick={() => onNavigate([])}
        >
          {t("Analytics.Widgets.Usage.Explorer.AllLocations", "All locations")}
        </button>
      )}
      {breadcrumbs.map((breadcrumb, index) => (
        <span key={breadcrumb.locationId} className="flex items-center gap-2">
          <span aria-hidden className="text-gray-400">
            /
          </span>
          {index === breadcrumbs.length - 1 ? (
            <span className="font-semibold text-gray-700">
              {breadcrumb.locationName}
            </span>
          ) : (
            <button
              type="button"
              className="font-semibold text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
              onClick={() =>
                onNavigate(
                  breadcrumbs
                    .slice(0, index + 1)
                    .map((item) => item.locationId),
                )
              }
            >
              {breadcrumb.locationName}
            </button>
          )}
        </span>
      ))}
    </nav>
  );
}

function getSelectedLocationBreadcrumbs(
  explorer: UsageAccountabilityExplorerDto,
  breadcrumbIds: number[],
) {
  return breadcrumbIds
    .map((locationId, index) =>
      explorer.locationRows
        .flatMap((row) => row.breadcrumb)
        .find(
          (node) =>
            node.locationId === locationId &&
            explorer.locationRows.some(
              (row) => row.breadcrumb[index]?.locationId === locationId,
            ),
        ),
    )
    .filter(
      (
        node,
      ): node is {
        locationId: number;
        locationName: string;
      } => !!node,
    );
}

function getAttentionLabel(
  attentionCardId: UsageAttentionCardId | undefined,
  t: TFunction,
) {
  if (attentionCardId === "EMPLOYEES_NOT_STARTED") {
    return t(
      "Analytics.Widgets.Usage.Attention.NotStarted.Title",
      "Employees not started",
    );
  }

  if (attentionCardId === "WAITING_ON_MANAGER_STALLED") {
    return t(
      "Analytics.Widgets.Usage.Attention.WaitingOnManager.Title",
      "Waiting on manager for 14+ days",
    );
  }

  return null;
}

export default UsageAccountabilityExplorer;
